import React from "react";
import { Radio, Tooltip } from "antd";
import { isNumber } from "lodash";

/**
 * The "card" radio style: each option is a bordered tile that highlights when
 * selected, for the cases where the choice is consequential enough to deserve
 * more than a bare dot in a list.
 *
 * Every state is expressed against AntD's own wrapper classes on the label
 * element itself, so the style needs no `checked` prop and works unchanged
 * inside a `Radio.Group`. It is applied to the label rather than a wrapping
 * div deliberately: only the label activates the input, so padding on an outer
 * element would leave a ring of card that looks clickable and is not.
 *
 * Width follows the container by default — a tile stretches in a column layout
 * and sizes to its content in a wrapped row. Pass `cardWidth` to fix every tile
 * to the same width, or `w-full` via `className` to fill the row.
 *
 * Exported so the style can be reused on a bare AntD `Radio` too.
 */
export const RADIO_CARD_CLASS = [
  "!m-0 inline-flex items-center rounded-lg border border-solid px-4 py-2.5 transition-colors",
  "border-default",
  // Brand primary — follows the active brand and light/dark mode.
  "[&:not(.ant-radio-wrapper-disabled):hover]:!border-primary",
  "[&.ant-radio-wrapper-checked]:!border-primary [&.ant-radio-wrapper-checked]:bg-primary-bg",
  // A disabled group still has to say which option is selected, so only the
  // options that were *not* chosen dim; the chosen one keeps its label colour
  // (`text-default`) and its primary-coloured dot.
  "[&.ant-radio-wrapper-disabled:not(.ant-radio-wrapper-checked)]:opacity-60",
  "[&.ant-radio-wrapper-checked.ant-radio-wrapper-disabled_span]:!text-default",
  "[&.ant-radio-wrapper-checked.ant-radio-wrapper-disabled_.ant-radio-inner]:!border-primary",
  "[&.ant-radio-wrapper-checked.ant-radio-wrapper-disabled_.ant-radio-inner::after]:!bg-primary",
].join(" ");

// How a group renders its options: classic radio dots, or a joined segmented
// button group (antd's `optionType`).
export const RADIO_OPTION_TYPE = {
  DEFAULT: "default",
  BUTTON: "button",
};

/**
 * CustomRadio - A reusable radio button component with optional tooltip
 *
 * @param {boolean} checked - Whether the radio is checked
 * @param {function} onChange - Callback function when radio state changes
 * @param {boolean} disabled - Whether the radio is disabled
 * @param {string} value - Value of the radio button
 * @param {string} size - Size of the radio button ('small', 'middle', 'large')
 * @param {string} tooltip - Tooltip text to display on hover
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {ReactNode} children - Content to display next to radio (for Radio.Group usage)
 * @param {ReactNode} description - Optional helper text rendered under the label.
 *                                  When set, the label is emphasized and the
 *                                  description appears as muted sub-text (title +
 *                                  subtitle layout). Radio aligns to the top.
 * @param {boolean} card - Render as a bordered card tile (see RADIO_CARD_CLASS)
 * @param {number|string} cardWidth - Fixed width for a card tile, so a row of
 *                                    them lines up instead of each sizing to its
 *                                    own label. A number is treated as px; a
 *                                    string is used verbatim ("12rem", "30%").
 *                                    Omit it and the tile sizes to its content.
 * @param {string} tooltipPlacement - Placement of tooltip ('top', 'bottom', 'left', 'right')
 * @param {object} tooltipProps - Additional props for tooltip component
 */
const CustomRadio = ({
  checked,
  onChange,
  disabled = false,
  value,
  // Accepted and swallowed: antd sizes radios from the enclosing Radio.Group,
  // not the individual Radio, so forwarding this would land it on the DOM input.
  // eslint-disable-next-line no-unused-vars
  size = "middle",
  tooltip,
  className = "",
  style = {},
  children,
  description,
  card = false,
  cardWidth,
  tooltipPlacement = "top",
  tooltipProps = {},
  ...rest
}) => {
  // A fixed width has to stop the tile flexing too, or a tight row shrinks the
  // cards back to unequal sizes. Caller `style` is spread last so it can still
  // override.
  const cardStyle =
    card && cardWidth
      ? { width: isNumber(cardWidth) ? `${cardWidth}px` : cardWidth, ...style }
      : style;

  const radioProps = {
    checked,
    onChange,
    disabled,
    value,
    // Top-align the dot so it sits on the same line as the label (not centered
    // against the whole label+description block). `items-start` overrides
    // antd's default wrapper alignment.
    className: `${card ? RADIO_CARD_CLASS : ""} ${card && cardWidth ? "shrink-0" : ""} ${
      description ? "items-start" : ""
    } ${className}`.trim(),
    style: cardStyle,
    ...rest,
  };

  // Label on the same row as the dot; description on the next row, left-aligned
  // with the label (the column starts to the right of the dot).
  const content = description ? (
    <span className="flex flex-col">
      <span className="font-medium text-sm text-default">{children}</span>
      <span className="text-[12px] leading-snug text-secondary">{description}</span>
    </span>
  ) : (
    children
  );

  const radioElement = <Radio {...radioProps}>{content}</Radio>;

  // If tooltip is provided, wrap with Tooltip component
  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement={tooltipPlacement} {...tooltipProps}>
        {radioElement}
      </Tooltip>
    );
  }

  return radioElement;
};

/**
 * CustomRadioGroup - A reusable radio group component
 *
 * @param {array} options - Array of option objects {label, value, disabled, tooltip, description}
 * @param {any} value - Selected value
 * @param {function} onChange - Callback when selection changes
 * @param {boolean} disabled - Whether the entire group is disabled
 * @param {boolean} card - Render every option as a bordered card tile
 * @param {number|string} cardWidth - Fixed width applied to every card tile
 * @param {string} size - Size of radio buttons ('small', 'middle', 'large')
 * @param {string} direction - Layout direction ('horizontal' or 'vertical').
 *                             Ignored for button options, which are always a
 *                             joined horizontal segmented control.
 * @param {string} optionType - 'default' for radio dots, 'button' for a
 *                             segmented button group (antd `optionType`)
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 */
export const CustomRadioGroup = ({
  options = [],
  value,
  onChange,
  disabled = false,
  card = false,
  cardWidth,
  size = "middle",
  direction = "vertical",
  optionType = RADIO_OPTION_TYPE.DEFAULT,
  className = "",
  style = {},
  ...rest
}) => {
  const isButton = optionType === RADIO_OPTION_TYPE.BUTTON;

  // Buttons are meant to sit flush against each other, so they keep antd's own
  // layout; dot radios get the flex row/column spacing. Card tiles are wider
  // than a bare radio, so a horizontal row of them wraps rather than
  // overflowing its container.
  const layoutClass = (() => {
    if (isButton) return "";
    if (direction !== "horizontal") return "flex flex-col gap-2";
    return `flex flex-row ${card ? "flex-wrap gap-3" : "gap-4"}`;
  })();

  return (
    <Radio.Group
      value={value}
      onChange={onChange}
      disabled={disabled}
      size={size}
      className={`${layoutClass} ${className}`.trim()}
      style={style}
      {...rest}
    >
      {options.map((option, index) => {
        const key = option.value ?? index;

        if (isButton) {
          const button = (
            <Radio.Button
              key={key}
              value={option.value}
              disabled={option.disabled || disabled}
              className={option.className}
              style={option.style}
            >
              {option.label}
            </Radio.Button>
          );
          // Radio.Button has no description slot — a tooltip is the only extra.
          return option.tooltip ? (
            <Tooltip key={key} title={option.tooltip}>
              {button}
            </Tooltip>
          ) : (
            button
          );
        }

        return (
          <CustomRadio
            key={key}
            value={option.value}
            // Card styling is a dot-radio affair: a Radio.Button is already a
            // bordered tile of antd's own.
            card={card}
            cardWidth={cardWidth}
            disabled={option.disabled || disabled}
            tooltip={option.tooltip}
            description={option.description}
            className={option.className}
            style={option.style}
          >
            {option.label}
          </CustomRadio>
        );
      })}
    </Radio.Group>
  );
};

export default CustomRadio;
